import type { Action } from 'state/actions';
import { addOrReplaceKey, patchKey, removeKey } from 'state/utils/collection';

import type { FeedbackState } from '../model';

export function feedbackReducer(state: FeedbackState = {}, action: Action): FeedbackState {
  switch (action.type) {
    case 'MISSED_DEAL_FEEDBACK_INIT':
      return addOrReplaceKey(state, action.tabId, {
        pricerId: action.pricerId,
        submissionStatus: 'idle' as const,
        submissionRequested: false,
        additionalInformation: {
          competitionParametersReferential: [],
          referentialStatus: 'idle' as const,
          isSgPositionning: false,
          isClientTraded: true,
        },
      });
    case 'MISSED_DEAL_FEEDBACK_SET_WITH_ADDITIONAL_INFORMATION':
      return patchKey(state, action.tabId, () => ({ withAdditionalInfo: action.withAdditionalInfo }));
    case 'MISSED_DEAL_FEEDBACK_REQUESTED':
      return patchKey(state, action.tabId, s => ({
        additionalInformation: {
          ...s.additionalInformation,
          referentialStatus: 'loading' as const,
        },
      }));
    case 'MISSED_DEAL_FEEDBACK_REFERENTIAL_RECEIVED':
      return patchKey(state, action.tabId, s => ({
        additionalInformation: {
          ...s.additionalInformation,
          competitionParametersReferential: action.parameters.parameters,
          initialPriceEnhancement: action.parameters.initialPriceEnhancement,
          referentialStatus: 'success' as const,
        },
      }));
    case 'MISSED_DEAL_FEEDBACK_REFERENTIAL_REQUEST_FAILED':
      return patchKey(state, action.tabId, s => ({
        technicalError: action.technicalError,
        additionalInformation: {
          ...s.additionalInformation,
          referentialStatus: 'error' as const,
        },
      }));
    case 'MISSED_DEAL_FEEDBACK_SUBMISSION_REQUESTED':
      return patchKey(state, action.tabId, () => ({
        submissionRequested: true,
      }));

    case 'MISSED_DEAL_FEEDBACK_SUBMISSION_REQUEST_CONFIRMED':
      return patchKey(state, action.tabId, () => ({
        submissionStatus: 'loading' as const,
        submissionStartDateInMilliseconds: action.startDateInMilliseconds,
        submissionEstimatedTimeInMilliseconds: action.estimatedTimeInMilliseconds,
      }));

    case 'MISSED_DEAL_FEEDBACK_SUBMISSION_REQUEST_DISCARDED':
      return patchKey(state, action.tabId, () => ({
        submissionRequested: false,
      }));

    case 'MISSED_DEAL_FEEDBACK_SUBMITTED':
      return patchKey(state, action.tabId, () => ({
        ack: action.ack,
        submissionStatus: 'success' as const,
        submissionStartDateInMilliseconds: undefined,
        submissionEstimatedTimeInMilliseconds: undefined,
      }));

    case 'MISSED_DEAL_FEEDBACK_SUBMISSION_REQUEST_FAILED':
      return patchKey(state, action.tabId, () => ({
        submissionStatus: 'error' as const,
        technicalError: action.technicalError,
        submissionStartDateInMilliseconds: undefined,
        submissionEstimatedTimeInMilliseconds: undefined,
      }));

    case 'MISSED_DEAL_FEEDBACK_RESET':
      return patchKey(state, action.tabId, s => ({
        withAdditionalInfo: undefined,
        comment: undefined,
        additionalInformation: {
          ...s.additionalInformation,
          isSgPositionning: false,
          sgPosition: undefined,
          numberOfIssuers: undefined,
          isClientTraded: true,
          priceEnhancement: undefined,
          referentialStatus:
            s.additionalInformation.referentialStatus !== 'success' ? ('idle' as const) : ('success' as const),
        },
        submissionStatus: 'idle' as const,
        submissionRequested: false,
        ack: undefined,
        technicalError: undefined,
        submissionStartDateInMilliseconds: undefined,
        submissionEstimatedTimeInMilliseconds: undefined,
      }));

    case 'MISSED_DEAL_FEEDBACK_COMMENT_CHANGED':
      return patchKey(state, action.tabId, () => ({
        comment: action.comment,
      }));

    case 'MISSED_DEAL_FEEDBACK_IS_SG_POSITIONNING_CHANGED':
      return patchKey(state, action.tabId, s => ({
        additionalInformation: {
          ...s.additionalInformation,
          isSgPositionning: action.isSgPositionning,
          sgPosition: undefined,
          numberOfIssuers: undefined,
        },
      }));

    case 'MISSED_DEAL_FEEDBACK_SG_POSITION_CHANGED':
      return patchKey(state, action.tabId, s => ({
        additionalInformation: {
          ...s.additionalInformation,
          sgPosition: action.sgPosition,
        },
      }));

    case 'MISSED_DEAL_FEEDBACK_NUMBER_OF_ISSUERS_CHANGED':
      return patchKey(state, action.tabId, s => ({
        additionalInformation: {
          ...s.additionalInformation,
          numberOfIssuers: action.numberOfIssuers,
        },
      }));

    case 'MISSED_DEAL_FEEDBACK_CLIENT_TRADED_CHANGED':
      return patchKey(state, action.tabId, s => ({
        additionalInformation: {
          ...s.additionalInformation,
          isClientTraded: action.isClientTraded,
        },
      }));

    case 'MISSED_DEAL_FEEDBACK_PRICE_ENHANCED_CHANGED':
      return patchKey(state, action.tabId, s => ({
        additionalInformation: {
          ...s.additionalInformation,
          priceEnhancement: action.isPriceEnhanced ? s.additionalInformation.initialPriceEnhancement : undefined,
        },
      }));

    case 'MISSED_DEAL_FEEDBACK_PRICE_ENHANCED':
      return patchKey(state, action.tabId, s => ({
        additionalInformation: {
          ...s.additionalInformation,
          priceEnhancement: {
            ...s.additionalInformation.priceEnhancement!,
            [action.field]: action.value,
          },
        },
      }));
    case 'QUOTE_STALE':
      if (!action.hasFormChanged || (action.workspace !== 'static' && action.workspace !== 'dynamic')) {
        return state;
      }
      return removeKey(state, action.tabId);

    case 'QUOTE_REMOVED':
      if (action.workspace !== 'static' && action.workspace !== 'dynamic') {
        return state;
      }
      return removeKey(state, action.tabId);

    default:
      return state;
  }
}
